import type { ZoneExportData } from "./data-fetcher";
import { PIN_PLAN_COLORS as PIN_COLORS } from "../palette";
import { originalToVisual } from "@/core/utils/plan-rotation";
import { computeMmPerPixel, layoutPins } from "./plan-layout";

// Résolution minimale d'export : 150 DPI sur largeur A4 (21 cm) = 1240 px.
// Si le plan source est plus petit, Konva upscale le rendu vectoriel (pins, textes)
// via pixelRatio tout en gardant le plan à sa résolution native.
const MIN_EXPORT_WIDTH_PX = 1240;

// Tailles cibles à l'impression, en mm sur le papier (converties en px du plan via
// mmPerPixel). Pin : au-dessus du minimum utilisateur 6 mm / 8 pts.
const TARGET_PIN_DIAMETER_MM = 7;
const LEADER_LINE_WIDTH_MM = 0.3;
const ANCHOR_DOT_DIAMETER_MM = 1.5;
const ANCHOR_DOT_STROKE_MM = 0.15;
// Jeu entre le bord d'une pastille et un point réel, pour que le point reste lisible
const ANCHOR_GAP_MM = 0.4;
const LEADER_COLOR = "#333333";

/**
 * Génère un PNG du plan annoté avec les pins colorés.
 * Utilise un Konva Stage offscreen (non monté dans le DOM visible).
 * Retourne null si la zone n'a pas de plan.
 */
export async function generateAnnotatedPlanBlob(
  zoneData: ZoneExportData
): Promise<Blob | null> {
  // Si le plan n'est pas en cache local (IndexedDB), tenter de le télécharger depuis le serveur
  let planBlobSource = zoneData.zone.planBlob;
  if (!planBlobSource && zoneData.zone.planUrl) {
    try {
      const token = localStorage.getItem("ultra-mesure-token");
      const headers: Record<string, string> = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const resp = await fetch(zoneData.zone.planUrl, { headers });
      if (resp.ok) {
        planBlobSource = await resp.arrayBuffer();
      }
    } catch {
      // Serveur indisponible → pas de plan
    }
  }
  if (!planBlobSource) return null;

  const Konva = (await import("konva")).default;

  // Convertir ArrayBuffer → Blob si nécessaire (Dexie stocke en ArrayBuffer pour Safari iOS)
  const planBlob = planBlobSource instanceof Blob
    ? planBlobSource
    : new Blob([planBlobSource], { type: "image/jpeg" });

  // Charger l'image du plan
  const img = await loadImageFromBlob(planBlob);
  const rawWidth = img.width;
  const rawHeight = img.height;

  // Appliquer la rotation définie par l'utilisateur
  const rotation = zoneData.zone.planRotation ?? 0;
  const isRotated90 = rotation === 90 || rotation === 270;
  const planWidth = isRotated90 ? rawHeight : rawWidth;
  const planHeight = isRotated90 ? rawWidth : rawHeight;

  // Échelle d'impression : combien de mm par pixel du plan une fois dans le PDF.
  // La légende n'est plus cuite dans le canvas (rendue en pdfmake, cf.
  // buildPlanLegend de pdf-sections.ts), donc la hauteur à contraindre = le plan seul.
  const mmPerPixel = computeMmPerPixel(planWidth, planHeight);
  const totalHeight = planHeight;

  // Créer un container offscreen
  const container = document.createElement("div");
  container.style.cssText =
    "position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;overflow:hidden";
  document.body.appendChild(container);

  const stage = new Konva.Stage({
    container,
    width: planWidth,
    height: totalHeight,
  });

  const layer = new Konva.Layer();
  stage.add(layer);

  // Image du plan avec rotation appliquée
  const planImage = new Konva.Image({
    image: img,
    x: 0,
    y: 0,
    width: rawWidth,
    height: rawHeight,
  });

  if (rotation !== 0) {
    // Rotation autour du centre de l'image source, puis repositionnement
    planImage.rotation(rotation);
    if (rotation === 90) {
      planImage.x(planWidth);
      planImage.y(0);
    } else if (rotation === 180) {
      planImage.x(planWidth);
      planImage.y(planHeight);
    } else if (rotation === 270) {
      planImage.x(0);
      planImage.y(planHeight);
    }
  }

  layer.add(planImage);

  // Taille des pins calibrée pour 7 mm de diamètre à l'impression
  const pinRadius = (TARGET_PIN_DIAMETER_MM / 2) / mmPerPixel;
  const fontSize = pinRadius * 0.9;

  // Collecter tous les pins (modèle B : zone → pins directement)
  const allPins: { pin: typeof zoneData.pins[0]["pin"]; color: string }[] = [];
  for (const pinData of zoneData.pins) {
    allPins.push({
      pin: pinData.pin,
      color: pinData.mesure?.codeCouleur ?? "default",
    });
  }

  // Les pins sont stockés dans l'espace de l'image d'origine : les ramener dans
  // l'espace du plan tourné avant tout calcul, sinon ils sont dessinés au mauvais
  // endroit dès que l'utilisateur a tourné le plan.
  // Puis placer les pastilles : celles des pins trop proches sont déportées à côté
  // de leur point réel (espacement min entre pastilles = 2.2× le rayon).
  const resolvedPositions = layoutPins(
    allPins.map((p) => originalToVisual(p.pin.x, p.pin.y, rotation, rawWidth, rawHeight)),
    {
      pinRadius,
      minSeparation: pinRadius * 2.2,
      anchorClearance: (ANCHOR_DOT_DIAMETER_MM / 2 + ANCHOR_GAP_MM) / mmPerPixel,
      bounds: { width: planWidth, height: planHeight },
    }
  );

  // Passe 1 : traits de liaison et points réels des pins déportés. Dessinés
  // avant TOUTES les pastilles pour qu'aucun trait ne barre un numéro.
  for (let i = 0; i < allPins.length; i++) {
    const pos = resolvedPositions[i];
    if (!pos.moved) continue;
    const fillColor = PIN_COLORS[allPins[i].color] ?? PIN_COLORS.default;

    layer.add(
      new Konva.Line({
        points: [pos.origX, pos.origY, pos.x, pos.y],
        stroke: LEADER_COLOR,
        strokeWidth: LEADER_LINE_WIDTH_MM / mmPerPixel,
      })
    );
    // Point à la position réelle de la mesure
    layer.add(
      new Konva.Circle({
        x: pos.origX,
        y: pos.origY,
        radius: (ANCHOR_DOT_DIAMETER_MM / 2) / mmPerPixel,
        fill: fillColor,
        stroke: LEADER_COLOR,
        strokeWidth: ANCHOR_DOT_STROKE_MM / mmPerPixel,
      })
    );
  }

  // Passe 2 : pastilles numérotées aux positions ajustées
  for (let i = 0; i < allPins.length; i++) {
    const { pin, color } = allPins[i];
    const pos = resolvedPositions[i];
    const fillColor = PIN_COLORS[color] ?? PIN_COLORS.default;

    // Ombre
    layer.add(
      new Konva.Circle({
        x: pos.x,
        y: pos.y + 2,
        radius: pinRadius,
        fill: "rgba(0,0,0,0.2)",
      })
    );

    // Cercle principal
    layer.add(
      new Konva.Circle({
        x: pos.x,
        y: pos.y,
        radius: pinRadius,
        fill: fillColor,
        stroke: "rgba(0,0,0,0.3)",
        strokeWidth: 1.5,
      })
    );

    // Numéro
    layer.add(
      new Konva.Text({
        x: pos.x - pinRadius,
        y: pos.y - pinRadius,
        width: pinRadius * 2,
        height: pinRadius * 2,
        text: String(pin.number),
        fontSize,
        fontStyle: "bold",
        fill: "#ffffff",
        align: "center",
        verticalAlign: "middle",
      })
    );
  }

  layer.draw();

  // Export en PNG, avec pixelRatio pour garantir une résolution minimale
  // Les éléments vectoriels (pins, textes, légende) sont re-rendus par Konva
  // à la résolution cible, donc toujours nets même sur un plan basse résolution.
  const pixelRatio = Math.max(1, MIN_EXPORT_WIDTH_PX / planWidth);
  const dataUrl = stage.toDataURL({ mimeType: "image/png", pixelRatio });
  const blob = await dataUrlToBlob(dataUrl);

  // Cleanup
  stage.destroy();
  document.body.removeChild(container);

  return blob;
}

/** Charge un Blob image en HTMLImageElement */
function loadImageFromBlob(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Impossible de charger l'image du plan"));
    };
    img.src = url;
  });
}

/** Convertit un data URL en Blob (compatible tous navigateurs, sans fetch) */
function dataUrlToBlob(dataUrl: string): Blob {
  const [header, data] = dataUrl.split(",");
  const mimeMatch = header.match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : "image/png";
  const bstr = atob(data);
  const n = bstr.length;
  const u8arr = new Uint8Array(n);
  for (let i = 0; i < n; i++) u8arr[i] = bstr.charCodeAt(i);
  return new Blob([u8arr], { type: mime });
}
